/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, JobDetailsModel } from '@gh/builder-shared';
import { AelEvents, BuilderPermission, getAelEventsArray } from '@gh/builder-shared-data';
import { DataSet, DateTimeTypes, getRouteInitData, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { translationTuple } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { composeComparators, isNotNil, negateComparator, valueComparator } from '@gh/core-util';
import { filter$, switchMap$ } from '@gh/rx/operators';
import { assign, isNil } from 'lodash';
import {
  getJobWarrantyTaskEnteredDate,
  getJobWarrantyTaskId,
  JobDetailsWarrantyTaskDialogConfig,
} from '../../../resources/job-details-tab';
import { JobDetailsTabBusinessService } from '../../../resources/job-details-tab/job-details-tab-business.service';
import { JobWarrantyTask } from '../../../resources/job-details-tab/job-details-tab-model';
import { jobDetailsWarrantyTaskDialogRoute } from '../job-details-warranty-task-details-dialog/job-details-warranty-task-details-dialog.component';

@Component({
  standalone: false,
  selector: 'gh-job-details-warranty-task-list-tab',
  templateUrl: 'job-details-warranty-task-list-tab.component.html',
})
export class JobDetailsWarrantyTabComponent implements OnInit {
  gridDefinition: LayoutBasedGridDefinition;
  warrantySet: DataSet<JobWarrantyTask>;
  outstandingOnly: boolean = false;
  editable: boolean;
  jobId: ID;

  constructor(private route: ActivatedRoute,
              private model: JobDetailsModel,
              private userContext: UserContext,
              private gridDefinitionFactory: GridDefinitionFactory,
              private businessService: JobDetailsTabBusinessService,
              private notifications: NotificationService,
              private waitings: WaitingService,
              private navigationService: BuilderNavigationService,
              private dialogService: DialogService) {
  }

  ngOnInit(): void {
    const { gridLayout, jobId, warrantyList } = getRouteInitData(this.route);

    assign(this, { jobId });

    this.warrantySet = new DataSet<JobWarrantyTask>(
      getJobWarrantyTaskId,
      negateComparator(composeComparators(
        valueComparator(getJobWarrantyTaskEnteredDate),
        valueComparator(getJobWarrantyTaskId))),
      warrantyList);

    this.editable = this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
      && !this.model.basicInfo.isNotProceeding;

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        enteredDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        toBeCompletedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        completedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      },
      {
        getRowId: (params) => String(params.data.id)
      });
    this.gridDefinition.bind(gridLayout);

  }

  createTask(): void {
    this.dialogService.openWithInput(jobDetailsWarrantyTaskDialogRoute, <JobDetailsWarrantyTaskDialogConfig>{
      basicInfo: this.model.basicInfo,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((task: JobWarrantyTask) => this.warrantySet.create(task));
  }

  goToAel(task?: JobWarrantyTask): void {
    this.navigationService.goToAel({
      label: 'lbl_tasks',
      eventTypeIds: isNil(task) ? getAelEventsArray(AelEvents.Job.WarrantyTask) : void 0,
      entityId: isNil(task) ? this.jobId : task.id,
      entityType: isNil(task) ? 'JOB' : 'JOB_MAINTENANCE_TASK',
    });
  }

  goToJobAel(): void {
    this.navigationService.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  onChangeShowOutstandingOnly(data: MatSlideToggleChange): void {
    this.outstandingOnly = data.checked;
    this.loadData();
  }

  onComplete(task: JobWarrantyTask): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_job_task_complete')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.businessService.completeWarrantyTask(task).pipe(this.waitings.operator())))
      .subscribe(
        (completedTask) => this.warrantySet.update(completedTask),
        this.notifications.processAll);
  }

  onOpen(task: JobWarrantyTask): void {
    this.dialogService.openWithInput(jobDetailsWarrantyTaskDialogRoute, <JobDetailsWarrantyTaskDialogConfig>{
        taskId: task.id,
        basicInfo: this.model.basicInfo,
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updatedTask: JobWarrantyTask) => this.warrantySet.update(updatedTask));
  }

  onDelete(task: JobWarrantyTask): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_job_task_delete')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.businessService.deleteWarrantyTask(task).pipe(this.waitings.operator())))
      .subscribe(
        () => this.warrantySet.remove(task),
        this.notifications.processAll);
  }

  isTaskCompleted(task: JobWarrantyTask): boolean {
    return isNotNil(task.completedDate);
  }

  private loadData(): void {
    this.businessService
      .getWarrantyTaskList({ outstandingOnly: this.outstandingOnly }).pipe(
      this.waitings.operator())
      .subscribe((warrantyList: JobWarrantyTask[]) => this.warrantySet.set(warrantyList));
  }
}
